import React, { useEffect, useState, useMemo } from 'react';
import { FetchAllConfigurations } from '../api/FetchAllConfigurations';
import { GetLaunch } from '../api/GetLaunch';
import ProgressBar from './ProgressBar';
import '../css/ProjectManager.css';

function ProjectManager({ interceptorsReady, setLaunchId, level, setCrmInfo }) {
  const [allConfigurations, setAllConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('budget');
  const [projectName, setProjectName] = useState('');
  const [tableSearch, setTableSearch] = useState('');

  const fetchAllConfigurations = async (level) => {
    try {
      setLoading(true);
      const result = await FetchAllConfigurations(level, setCrmInfo);
      setAllConfigurations(result);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigurationClick = async (config) => {
    try {
      setCrmInfo({
        budget: {
          id: config.budget_id,
          name: config.budget_name,
          revision:
            allConfigurations.filter((c) => config.budget_id === c.budget_id)
              .length + 1,
        },
      });
      const launch_string = await GetLaunch({
        config_id: config.configuration_id,
      });
      setLaunchId(launch_string.launch_id);
    } catch (error) {
      console.error('Failed to fetch launch string:', error);
    }
  };

  const createNewProject = async () => {
    try {
      setCrmInfo({
        budget: { name: projectName, revision: 1 },
      });
      const launch_string = await GetLaunch({ config_id: -1 });
      setLaunchId(launch_string.launch_id);
    } catch (error) {
      console.error('Failed to fetch launch string:', error);
    }
  };

  useEffect(() => {
    if (interceptorsReady) {
      fetchAllConfigurations(level);
    }
  }, [interceptorsReady, level]);

  const notProdEnv = process.env.REACT_APP_ENV !== 'production';
  const crm_link = notProdEnv
    ? 'https://ambaflex-acc.crm4.dynamics.com/main.aspx?appid=b311edf6-691a-ea11-a811-000d3a218624&forceUCI=1&pagetype=dashboard&id=3765da9a-91f4-4780-92c6-771ae69a6cbb&type=system&_canOverride=true'
    : 'https://ambaflex.crm4.dynamics.com/main.aspx?appid=d0e0adda-0f23-ee11-9cbd-6045bd90b5bd&pagetype=dashboard&id=cab77653-d9fc-e911-a813-000d3a2197b6&type=system&_canOverride=true';

  return (
    <div className="configurations">
      <ProgressBar setLaunchId={setLaunchId} />
      <div className="tabs">
        {['quoting', 'budget'].map((modeType) => (
          <button
            key={modeType}
            className={`tab-button ${mode === modeType ? 'active' : ''} `}
            onClick={() => setMode(modeType)}
          >
            {modeType} mode
          </button>
        ))}
      </div>

      <div className="prj-manager-main">
        {mode === 'quoting' ? (
          <>
            <p className="dark">
              To start a quote you need to enter from the CRM
            </p>
            <a href={crm_link}>
              Go to the crm{notProdEnv ? ' (test environment)' : ''}
            </a>
          </>
        ) : (
          <>
            <p className="dark">
              Configure your machine and get a price quickly.
            </p>
            <p className="dark">Your data will not be saved to the CRM.</p>

            <div className="project-manager-table-toolbar">
              <input
                type="text"
                className="shared-search-input"
                placeholder="Search project, config, user"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
              />
            </div>

            <div className="glass configuration-table-container">
              {loading ? (
                <div className="loading-message">
                  Loading configurations{' '}
                  <span className="dotted-spinner" aria-hidden="true"></span>
                </div>
              ) : (
                allConfigurations?.length > 0 && (
                  <BudgetConfigurationsTable
                    allConfigurations={allConfigurations}
                    handleConfigurationClick={handleConfigurationClick}
                    currency="EUR"
                    searchQuery={tableSearch}
                  />
                )
              )}
            </div>
            <div className="project-manager-button-container">
              <input
                type="text"
                className="input-md"
                placeholder="Project name"
                value={projectName}
                maxLength={100}
                onChange={(e) => setProjectName(e.target.value)}
              />

              <button
                className="button-md button-primary"
                onClick={() => {
                  const trimmed = projectName.trim();
                  if (!trimmed) return;
                  createNewProject(trimmed);
                  setProjectName('');
                }}
                disabled={projectName.trim().length === 0}
              >
                <span>+</span> New budget pricing
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BudgetConfigurationsTable({
  allConfigurations = [],
  handleConfigurationClick,
  currency = 'EUR',
  searchQuery = '',
}) {
  const toDate = (v) => (v ? new Date(v) : new Date(0));
  const formatDate = (v) =>
    v ? new Date(v).toLocaleString(navigator.language || 'en-US') : '';
  const nf = useMemo(
    () =>
      new Intl.NumberFormat(navigator.language || 'en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }),
    [currency]
  );

  const grouped = useMemo(() => {
    const map = new Map();
    for (const cfg of allConfigurations) {
      const list = map.get(cfg.budget_id) || [];
      list.push(cfg);
      map.set(cfg.budget_id, list);
    }

    const groups = Array.from(map.entries()).map(([budget_id, revisions]) => {
      revisions.sort(
        (a, b) =>
          toDate(b.configuration_date_created) -
          toDate(a.configuration_date_created)
      );

      const first = revisions[0];
      return {
        budget_id,
        budget_name: first?.budget_name ?? '',
        lastRevisionDate: first?.configuration_date_created ?? null,
        revisions,
      };
    });

    groups.sort(
      (a, b) =>
        toDate(b.lastRevisionDate).getTime() -
        toDate(a.lastRevisionDate).getTime()
    );

    return groups;
  }, [allConfigurations]);

  const filteredGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return grouped;

    return grouped.filter(({ budget_name, revisions }) => {
      const revisionText = revisions
        .map((rev) =>
          [rev.configuration_id, rev.username, rev.configuration_grossprice]
            .filter((v) => v !== null && v !== undefined)
            .join(' ')
        )
        .join(' ')
        .toLowerCase();

      return `${String(budget_name || '').toLowerCase()} ${revisionText}`.includes(
        query
      );
    });
  }, [grouped, searchQuery]);

  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (budgetId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(budgetId)) next.delete(budgetId);
      else next.add(budgetId);
      return next;
    });
  };

  return (
    <table>
      <thead>
        <tr>
          <th>Name</th>
          <th>Date</th>
          <th>Revision</th>
          <th>configuration ID</th>
          <th>Price</th>
          <th>User</th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        {filteredGroups.map(
          ({ budget_id, budget_name, lastRevisionDate, revisions }) => {
            const isOpen = expanded.has(budget_id);
            const revisionCount = revisions.length;
            const latestRevision = revisions[0] || {};
            const latestPrice = Number(
              latestRevision.configuration_grossprice ?? 0
            );
            return (
              <React.Fragment key={budget_id}>
                <tr className="budget-row">
                  <td>{budget_name}</td>
                  <td>{formatDate(lastRevisionDate)}</td>
                  <td>{revisionCount}</td>
                  <td>{latestRevision.configuration_id ?? '-'}</td>
                  <td>
                    {Number.isFinite(latestPrice)
                      ? nf.format(latestPrice)
                      : '-'}
                  </td>
                  <td>{latestRevision.username || '-'}</td>
                  <td className="open-revisions">
                    <button
                      className="button-sm button-secondary"
                      onClick={() => toggle(budget_id)}
                      aria-expanded={isOpen}
                      aria-controls={`rev-rows-${budget_id}`}
                      title={isOpen ? 'Hide revisions' : 'Show revisions'}
                    >
                      {isOpen ? '–' : '+'}
                    </button>
                  </td>
                </tr>

                {isOpen &&
                  revisions.map((rev, idx) => {
                    const revisionNumber = revisionCount - idx;
                    const priceNum = Number(rev.configuration_grossprice ?? 0);

                    return (
                      <tr
                        key={rev.configuration_id}
                        id={`rev-rows-${budget_id}`}
                        className="revision-row"
                      >
                        <td colSpan={1}></td>
                        <td>{formatDate(rev.configuration_date_created)}</td>
                        <td>{revisionNumber}</td>
                        <td>{rev.configuration_id}</td>
                        <td>
                          {Number.isFinite(priceNum)
                            ? nf.format(priceNum)
                            : '-'}
                        </td>

                        <td>{rev.username}</td>
                        <td>
                          <button
                            className="button-sm button-primary"
                            onClick={() => handleConfigurationClick?.(rev)}
                          >
                            Load
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </React.Fragment>
            );
          }
        )}
      </tbody>
    </table>
  );
}

export default ProjectManager;
